import { useMemo } from "react";
import Typography from "@mui/material/Typography";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import { Link, replace, useNavigate } from "react-router";
import { useTheme } from "@mui/material";
import "../assets/css/Dashboard.css";
import { useData } from "../contexts/DataContext";
import { breadcrumbStyles } from "../styles/breadcrumbs";

export default function BasicBreadcrumbs({ dashboardInfo, sqlLabInfo }: any) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { dashboardList } = useData();

  const parentDashboard = useMemo(() => {
    const parentId = dashboardInfo?.parent_dashboard;
    if (parentId == null) {
      return undefined;
    }
    return dashboardList.find((d) => d.id === parentId);
  }, [dashboardInfo?.parent_dashboard, dashboardList]);

  const childSubsection = useMemo(() => {
    if (!parentDashboard || !dashboardInfo?.section?.trim()) {
      return undefined;
    }
    const parentSection = parentDashboard.section?.trim() ?? "";
    const childSection = dashboardInfo.section.trim();
    if (
      !parentSection ||
      childSection.toLowerCase() === parentSection.toLowerCase()
    ) {
      return undefined;
    }
    return childSection;
  }, [parentDashboard, dashboardInfo?.section]);

  return (
    <Breadcrumbs
      separator="›"
      aria-label="breadcrumb"
      sx={{ mx: 0, flexWrap: "wrap", alignItems: "center" }}
    >
      <Link
        color="text.primary"
        to="/"
        key={"home"}
        onClick={(e) => {
          e.preventDefault();
          navigate("/");
        }}
        style={{ textDecoration: "none" }}
        className="styled-link"
      >
        <Typography
          color="text.primary"
          sx={{
            fontSize: "14px",
            fontWeight: 300,
            color: theme.palette.text.secondary,
          }}
        >
          {"Home"}
        </Typography>
      </Link>
      {dashboardInfo &&
        parentDashboard && [
          <Link
            color="text.primary"
            to="/"
            key="breadcrumb-parent-dashboard"
            onClick={(e) => {
              e.preventDefault();
              navigate(`/dashboard/${parentDashboard.id}`);
            }}
            style={{ textDecoration: "none" }}
            className="styled-link"
          >
            <Typography
              key="breadcrumb-section"
              component="span"
              sx={{
                fontSize: "14px",
                fontWeight: 300,
                color: theme.palette.text.secondary,
                display: "inline",
              }}
            >
              {parentDashboard.section}
              {" - "}
            </Typography>

            <Typography
              component="span"
              sx={{
                fontSize: "14px",
                fontWeight: 300,
                color: theme.palette.text.secondary,
                display: "inline",
              }}
            >
              {parentDashboard.title}
            </Typography>
          </Link>,
          <Link
            color="text.primary"
            to={`/dashboard/${dashboardInfo.id}`}
            key="breadcrumb-child-dashboard"
            onClick={(e) => {
              e.preventDefault();
              navigate(`/dashboard/${dashboardInfo.id}`);
            }}
            style={{ textDecoration: "none" }}
            className="styled-link"
          >
            {childSubsection ? (
              <Typography
                component="span"
                sx={breadcrumbStyles(theme).breadcrumbChildLinkText}
              >
                {childSubsection}
                {" - "}
              </Typography>
            ) : null}
            <Typography
              component="span"
              sx={breadcrumbStyles(theme).breadcrumbChildLinkText}
            >
              {dashboardInfo.title}
            </Typography>
          </Link>,
        ]}
      {dashboardInfo && !parentDashboard && (
        <Link
          color="text.primary"
          to={`/`}
          key={"dashboard"}
          onClick={(e) => {
            e.preventDefault();
            replace(`/dashboard/${dashboardInfo.id}`);
          }}
          style={{ textDecoration: "none" }}
          className="styled-link"
        >
          <Typography
            color="text.primary"
            key={"title"}
            sx={{
              fontSize: "14px",
              fontWeight: 300,
              color: theme.palette.text.secondary,
            }}
          >
            {`${dashboardInfo.section} ${
              dashboardInfo?.breadcrumps_sections?.length ? " > " : " - "
            }`}
            <span
              style={{ fontWeight: 300, color: theme.palette.text.secondary }}
            >
              {dashboardInfo.title}
            </span>
          </Typography>
        </Link>
      )}
      {dashboardInfo?.breadcrumps_sections?.length &&
        dashboardInfo?.breadcrumps_sections?.map(
          (data: string, index: number) => (
            <Typography
              color="text.primary"
              key={"custom_section"}
              sx={{
                fontSize: "14px",
                fontWeight:
                  index + 1 === dashboardInfo?.breadcrumps_sections?.length
                    ? 500
                    : 300,
                color:
                  index + 1 === dashboardInfo?.breadcrumps_sections?.length
                    ? "#455A64"
                    : theme.palette.text.secondary,
              }}
            >
              {data}
            </Typography>
          ),
        )}
      {sqlLabInfo && (
        <Typography
          color="text.primary"
          key={"sqllab_info"}
          sx={{
            fontSize: "14px",
            fontWeight: 300,
            color: theme.palette.text.secondary,
          }}
        >
          {`SQL Lab - `}
          <span style={{ fontWeight: 500, color: theme.palette.text.primary }}>
            {sqlLabInfo.name && `${sqlLabInfo.name}`}
          </span>
        </Typography>
      )}
    </Breadcrumbs>
  );
}
